import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LeistungslohnVariant } from './types';
import { MonoLeistungslohnDocument } from './MonoLeistungslohnDocument';
import { TeamLeistungslohnDocument } from './TeamLeistungslohnDocument';
import { LeistungslohnVariantToggle } from './LeistungslohnVariantToggle';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

interface ZusatzvereinbarungModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Initial / kontrollierter Variant — beim Öffnen und bei Prop-Änderung übernommen. */
    variant: LeistungslohnVariant;
    /** Optional: Parent über Umschalten im Modal informieren. */
    onVariantChange?: (variant: LeistungslohnVariant) => void;
}

const PRINT_ROOT_ID = 'leistungslohn-zusatz-print';

const PRINT_IFRAME_CSS = `
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    background: #fff;
    line-height: 1.55;
    margin: 0;
    padding: 18mm 16mm;
    font-size: 13px;
  }
  h1 {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 4px;
    text-align: center;
  }
  h2 {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #4b5563;
    margin: 0 0 16px;
    text-align: center;
  }
  h3 {
    font-size: 15px;
    font-weight: bold;
    margin: 18px 0 10px;
    text-transform: uppercase;
    border: 1px solid #d1d5db;
    background: #f3f4f6;
    padding: 8px;
    text-align: center;
  }
  h4 {
    font-size: 14px;
    font-weight: bold;
    margin: 18px 0 8px;
    border-bottom: 1px solid #d1d5db;
    padding-bottom: 4px;
  }
  p { margin: 0 0 10px; text-align: justify; }
  .print-break {
    page-break-before: always !important;
    break-before: page !important;
    margin-top: 0;
    padding-top: 8px;
  }
  .print-signatures-grid {
    display: table !important;
    width: 100% !important;
    margin-top: 40px;
  }
  .signature-col {
    display: table-cell !important;
    width: 50% !important;
    padding-right: 20px;
    vertical-align: top;
  }
  .fill-line {
    display: inline-block;
    border-bottom: 1px solid #000;
    width: 12rem;
    margin: 0 0.5rem;
    min-height: 1em;
    vertical-align: bottom;
  }
  .fill-line-sm {
    display: inline-block;
    border-bottom: 1px solid #000;
    width: 3rem;
    margin: 0 0.25rem;
    min-height: 1em;
    vertical-align: bottom;
  }
  .sig-line {
    border-bottom: 1px solid #000;
    height: 2rem;
    margin-bottom: 0.35rem;
  }
  .optional-box {
    border: 1px dashed #9ca3af;
    padding: 12px;
    margin-top: 12px;
    position: relative;
  }
  .optional-badge {
    position: absolute;
    top: -10px;
    left: 12px;
    background: #fff;
    padding: 0 6px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
  }
  .doc-panel {
    border: 1.5px solid #1f2937;
    background: #fafafa;
    padding: 20px 18px;
    margin: 4px 0 8px;
  }
  .doc-kicker {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #6b7280;
    margin: 0 0 16px;
  }
  .pdf-letterhead {
    text-align: center;
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
    margin: 0 0 18px;
  }
  .hidden { display: none !important; }
  @media print {
    .hidden.print\\:block, .print\\:block.hidden { display: block !important; }
    .print-break { page-break-before: always !important; break-before: page !important; }
    @page { margin: 14mm; size: A4; }
  }
`;

/** A4-Vorschau — PDF wie PEPapers (direkter Download), Druck isoliert per iframe. */
export const ZusatzvereinbarungModal: React.FC<ZusatzvereinbarungModalProps> = ({
    isOpen,
    onClose,
    variant,
    onVariantChange,
}) => {
    const [activeVariant, setActiveVariant] = useState<LeistungslohnVariant>(variant);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const printContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) setActiveVariant(variant);
    }, [isOpen, variant]);

    // Portal + body-lock: sonst liegt Toolbar unter Sticky-Header / Sidebar (Zoom-Stacking)
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleVariantChange = (next: LeistungslohnVariant) => {
        setActiveVariant(next);
        onVariantChange?.(next);
    };

    const isTeam = activeVariant === 'team';

    const getPrintHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById(PRINT_ROOT_ID)?.innerHTML ||
        '';

    const handleDownloadPdf = async () => {
        if (isPdfDownloading) return;
        const html = getPrintHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            const base = isTeam ? 'Zusatzvereinbarung_Teamleistungslohn' : 'Zusatzvereinbarung_MonoLeistungslohn';
            await downloadHtmlAsPdf({
                html,
                filename: `${base}_${dateIso}.pdf`,
                // Gleicher CSS-Pfad wie „Drucken“ — kein aggressives Klassen-Scrubbing
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                extraCss: PRINT_IFRAME_CSS,
            });
        } catch (err: any) {
            console.error('Zusatzvereinbarung PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handlePrintIsolated = () => {
        if (isPdfDownloading) return;
        const printContent = getPrintHtml();
        if (!printContent.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const title = isTeam ? 'Zusatzvereinbarung Teamleistungslohn' : 'Zusatzvereinbarung Mono-Leistungslohn';
        const doc = printWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>${title}</title>
<style>${PRINT_IFRAME_CSS}</style></head><body>${printContent}</body></html>`);
        doc.close();

        window.setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            window.setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        }, 400);
    };

    return createPortal(
        <div className="fixed inset-0 z-[200000] bg-stone-900/80 dark:bg-black/95 backdrop-blur-sm flex flex-col print:hidden overflow-hidden">
            <div className="flex-shrink-0 bg-pe-elevated dark:bg-[#0a0b10] border-b border-pe-border dark:border-[#2a2a2a] shadow-2xl">
                <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-widest text-xs sm:text-sm min-w-0">
                            <span className="block truncate">Zusatzvereinbarung (Vorschau)</span>
                            <span className="block text-[10px] font-bold text-pe-muted dark:text-slate-500 normal-case tracking-normal mt-0.5">
                                {isTeam ? 'Teamleistungslohn' : 'Mono-Leistungslohn'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap justify-end">
                            <button
                                type="button"
                                onClick={() => void handleDownloadPdf()}
                                disabled={isPdfDownloading}
                                className="bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg font-black uppercase tracking-widest shadow-[0_0_15px_rgba(230,194,153,0.3)] flex items-center gap-2 transition-all duration-300 text-[10px] sm:text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                            </button>
                            <button
                                type="button"
                                onClick={handlePrintIsolated}
                                disabled={isPdfDownloading}
                                className="bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] text-pe-accent-text dark:text-[#E6C299] px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg font-bold uppercase tracking-widest border border-[#E6C299]/40 transition-colors text-[10px] sm:text-xs disabled:opacity-60"
                            >
                                Drucken
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isPdfDownloading}
                                className="bg-stone-200 dark:bg-slate-800 hover:bg-red-100 dark:hover:bg-red-900/80 text-pe-text dark:text-white px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg border border-pe-border-strong dark:border-slate-600 shadow-xl transition-colors flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0 disabled:opacity-60"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                <span className="hidden sm:inline">Schließen</span>
                                <span className="sm:hidden">Zu</span>
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-center sm:justify-start">
                        <LeistungslohnVariantToggle
                            variant={activeVariant}
                            onChange={handleVariantChange}
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-8 flex justify-center custom-scrollbar">
                <div
                    id={PRINT_ROOT_ID}
                    ref={printContainerRef}
                    key={isTeam ? 'print-team' : 'print-mono'}
                    className="bg-white text-black w-full max-w-[210mm] h-max min-h-[297mm] shadow-[0_0_50px_rgba(0,0,0,0.5)] p-10 sm:p-16 font-serif relative text-sm leading-relaxed shrink-0"
                >
                    {isTeam ? (
                        <TeamLeistungslohnDocument key="doc-team" />
                    ) : (
                        <MonoLeistungslohnDocument key="doc-mono" />
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ZusatzvereinbarungModal;
